import {
	TextAreaInput,
	useResizeTextAreaInput,
} from '@qr-front/ui/components/TextareaInput'
import { cn } from '@qr-front/ui/utils'
import { qrTypeAtom } from 'features/generator/generatorState'
import { QR_TYPE } from 'features/generator/generatorTypes'
import { useAtomValue } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { ChangeEvent, useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import * as yup from 'yup'
import { InferType } from 'yup'

import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'
import { maxLength } from '@/utils'

import { useRhcFieldProps } from '../../components/RhcField'

export const SUMMARY_MAX_LENGTH = 250

export const getSummarySchema = (t: TTranslationFunction) =>
	yup.object({
		summary: yup.string().test('summary-validation', function (value) {
			if (value && value.toString().length >= SUMMARY_MAX_LENGTH) {
				return this.createError({
					message: t('ui__field_validation_errors__generic__max_length', {
						length: SUMMARY_MAX_LENGTH,
					}),
				})
			}
			return true
		}),
	})

export type TSummaryFormField = InferType<ReturnType<typeof getSummarySchema>>

export const summaryAtom = atomWithReset<string | undefined>('')

export const resetSummaryAtom = () => {
	getStore().set(summaryAtom, RESET)
}

export const SummarySection = () => {
	const t = useTranslations()
	const qrType = useAtomValue(qrTypeAtom)
	const isBusinessPageQrType = QR_TYPE.BusinessPage === qrType

	const form = useFormContext<TSummaryFormField>()

	const { inputProps, error } = useRhcFieldProps({
		form,
		path: 'summary',
		atom: summaryAtom,
		onChangeTransform: (event) =>
			maxLength(SUMMARY_MAX_LENGTH)(event as ChangeEvent<HTMLInputElement>),
	})

	const enhancedInputProps = {
		...inputProps,
		value: form.watch('summary') || '',
	}

	const summary = useAtomValue(summaryAtom)

	useEffect(() => {
		if (summary && form.getValues('summary') !== summary) {
			form.setValue('summary', summary)
		}
	}, [summary, form])

	const textAreaRef = useRef<HTMLTextAreaElement | null>(null)

	useResizeTextAreaInput(textAreaRef, summary || '', '96px')

	return (
		<TextAreaInput
			{...enhancedInputProps}
			ref={textAreaRef}
			error={error}
			className={cn('resize-none', isBusinessPageQrType ? '-mb-1.5' : 'mb-0')}
			label={
				isBusinessPageQrType
					? t(
							'generator__qr_content_screen__business_page_type__sections__summary__title',
						)
					: t('generator__content_form_section__about__summary_section__text')
			}
			placeholder={
				isBusinessPageQrType
					? t(
							'generator__content_form_section__business_page__summary_section__text__placeholder',
						)
					: t(
							'generator__content_form_section__about__summary_section__text__placeholder',
						)
			}
		/>
	)
}
